import { Component } from 'react'
import { useLocation, Link } from 'react-router-dom'

// A render-time error anywhere in src/ currently unmounts the ENTIRE app —
// there is no error boundary anywhere. That's not hypothetical: it happened
// for real when a component rendered a non-array 401 error body and threw,
// taking down the whole site, including the SessionExpiredModal that exists
// specifically to recover from an expired/invalid session. This boundary
// exists to stop a single component's crash from destroying everything else
// on the page (see App.jsx for where it's placed and why placement matters).
//
// Error boundaries must be class components — getDerivedStateFromError and
// componentDidCatch have no hook equivalent.
//
// What this catches, and what it doesn't: React error boundaries only catch
// errors thrown during render, in lifecycle methods, and in constructors of
// the tree below them. They do NOT catch errors thrown from event handlers,
// from async code (promise/.then/.catch callbacks, setTimeout, etc.), during
// SSR, or thrown by the boundary's own render. Concretely, for the incident
// that motivated this file: the crash was caught not because this boundary
// intercepted the failing fetch, but because the bad (non-array) value made
// it into component state and *then* threw during `.map()` at render time —
// a render-time error, which is exactly this boundary's job. Had the error
// instead been thrown inside the fetch's own .then()/.catch() chain, it
// would have sailed straight past this boundary uncaught. That's why
// guarding response shapes at the fetch site (see the Array.isArray(...) ?
// ... : [] guards in AssignSecretModal.jsx and FeywildPage.jsx) remains
// necessary — this boundary is a net for what slips through those guards
// into render, not a replacement for having them.
//
// Styled entirely with inline styles (not a shared stylesheet or the .ss-btn
// classes elsewhere in the app): this is the UI of last resort, rendered
// specifically when something has already gone wrong, so it should depend on
// as little of the rest of the app as possible — no risk of a settings-page
// restyle silently reshaping the crash fallback, and no stylesheet pulled
// into every route's bundle just for the rare case this renders at all.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // Logged, not swallowed — this is the only record of what broke once
    // the fallback UI below replaces the crashed tree.
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      // Shared base for both actions below, styled only with the five CSS
      // custom properties already used elsewhere in this file — no reach
      // into --accent-teal or any other token, and no dependency on the
      // .ss-btn classes (see the comment above the class for why).
      const buttonBaseStyle = {
        padding: '0.75rem 1.5rem',
        borderRadius: '8px',
        border: '1px solid var(--border-color)',
        fontWeight: 600,
        fontSize: '0.95rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-block',
      }

      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-primary)',
            padding: '2rem',
          }}
        >
          <div
            style={{
              maxWidth: '420px',
              width: '100%',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '2.5rem',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            }}
          >
            <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
              Something went wrong
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.75rem' }}>
              This part of the page hit an error. Reloading usually fixes it —
              if it keeps happening, head back to your campaigns.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  ...buttonBaseStyle,
                  background: 'var(--text-primary)',
                  color: 'var(--bg-primary)',
                }}
              >
                Reload
              </button>
              <Link
                to="/campaigns"
                style={{
                  ...buttonBaseStyle,
                  background: 'transparent',
                  color: 'var(--text-primary)',
                }}
              >
                Back to campaigns
              </Link>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Class components can't use hooks, so this function wrapper reads the
// current route via useLocation() and keys the class on the pathname. A
// tripped boundary otherwise stays tripped forever (state lives in the
// mounted instance), which would strand a user on the fallback UI even
// after they navigate elsewhere. Keying on pathname forces React to
// unmount/remount the class instance on every route change, resetting
// `hasError` back to false automatically.
export function RouteResetErrorBoundary({ children }) {
  const location = useLocation()
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
}

export default ErrorBoundary
